import React from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';

interface BentoCellProps {
  children: React.ReactNode;
  className?: string;
  colSpan?: number;
  rowSpan?: number;
  glowColor?: string;
  delay?: number;
  style?: React.CSSProperties;
}

export function BentoCell({
  children,
  className = '',
  colSpan = 4,
  rowSpan = 1,
  glowColor = 'rgba(59,130,246,0.15)',
  delay = 0,
  style,
}: BentoCellProps) {
  const x = useMotionValue(0.5);
  const y = useMotionValue(0.5);
  const springX = useSpring(x, { stiffness: 150, damping: 20 });
  const springY = useSpring(y, { stiffness: 150, damping: 20 });
  const spotlightX = useTransform(springX, [0, 1], ['0%', '100%']);
  const spotlightY = useTransform(springY, [0, 1], ['0%', '100%']);

  const handleMouse = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width);
    y.set((e.clientY - rect.top) / rect.height);
  };

  return (
    <motion.div
      className={`bento-cell ${className}`}
      style={{
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`,
        ...style,
      }}
      initial={{ opacity: 0, y: 20, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      whileHover={{ scale: 1.015, transition: { duration: 0.2 } }}
      onMouseMove={handleMouse}
    >
      <motion.div
        className="bento-spotlight"
        style={{
          background: useTransform(
            [spotlightX, spotlightY],
            ([sx, sy]) =>
              `radial-gradient(400px circle at ${sx} ${sy}, ${glowColor}, transparent 60%)`
          ),
        }}
      />
      <div className="bento-cell-inner">{children}</div>
    </motion.div>
  );
}
